import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Loader2,
  Send,
  X,
  AlertTriangle,
  AlertCircle,
  Video,
  Clock,
  FileText,
  Hash,
  Ban,
  Gavel,
  ShoppingBag,
  Code,
  Shield,
  HelpCircle,
  Ticket,
  MapPin,
  MessageSquare,
  Zap,
  Car,
  Skull,
  LogOut,
  MessageSquareWarning
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { useTickets, generateUniqueTicketId } from '../../contexts/TicketContext';
import { usePunishment } from '../../contexts/PunishmentContext';
import { FLOW_CONFIG } from './TicketFlowConfig';
import { formatPunishmentDuration } from '../../utils/punishmentUtils';
import { useDynamicGradient } from '../../hooks/useDynamicGradient';
import R2Upload from '../Staff/R2Upload';


/**
 * TicketCreate (Wizard Version)
 * Implementa um fluxo conversacional passo-a-passo.
 */
const TicketCreate = () => {
  const { user, logout } = useAuth();
  const { createTicket, tickets, loading: loadingContext, faqs, getSimilarTickets, categories } = useTickets();
  // Consolidate all usePunishment destructuring in one place
  const { getActiveCharacterPunishments, createAppeal, getActivePunishments } = usePunishment();
  const navigate = useNavigate();
  const dynamicGradient = useDynamicGradient();

  const [pendingTicketId] = useState(() => generateUniqueTicketId());

  // State do Wizard
  const [currentStepId, setCurrentStepId] = useState('start');
  const [history, setHistory] = useState(['start']); // Pilha de passos para voltar
  const [answers, setAnswers] = useState({}); // Mapa de respostas { stepId: value }
  const [foundPunishments, setFoundPunishments] = useState([]); // Punições encontradas
  const [selectedPunishment, setSelectedPunishment] = useState(null); // Punição selecionada para recurso
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward
  const [loading, setLoading] = useState(false); // Added loading state

  // Cupom 70 Settings
  const [cupomSettings, setCupomSettings] = useState(null);
  const [cupomTimeLeft, setCupomTimeLeft] = useState('');

  // --- DUPLICATE TICKET CHECK ---
  const [duplicateTicket, setDuplicateTicket] = useState(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [pendingStepValue, setPendingStepValue] = useState(null);
  const [pendingNextStepId, setPendingNextStepId] = useState(null);

  // --- TICKET BAN LOGIC ---
  const [isBanned, setIsBanned] = useState(false);
  const [banPunishment, setBanPunishment] = useState(null);
  const [appealText, setAppealText] = useState('');
  const [appealSubmitted, setAppealSubmitted] = useState(false);
  const [appealDenied, setAppealDenied] = useState(false);
  const [existingAppealTicketId, setExistingAppealTicketId] = useState(null);

  // Estados para sugestões
  const [suggestedFaqs, setSuggestedFaqs] = useState([]);
  const [similarTickets, setSimilarTickets] = useState([]);

  // Referência para auto-focus
  const inputRef = useRef(null);

  // Configuração do passo atual com suporte a Categorias Dinâmicas
  const currentStep = React.useMemo(() => {
    const rawStep = FLOW_CONFIG[currentStepId];

    // Se não for o passo inicial, retorna o padrão do Config
    if (currentStepId !== 'start') {
      return rawStep;
    }

    // Se tivermos categorias do banco, usamos ELAS como fonte da verdade
    if (categories && categories.length > 0) {
      const ICON_MAP = {
        'AlertCircle': AlertCircle,
        'ShoppingBag': ShoppingBag,
        'Code': Code,
        'Shield': Shield,
        'HelpCircle': HelpCircle,
        'Ticket': Ticket,
        'Gavel': Gavel,
        'Ban': Ban,
        'MessageSquare': MessageSquare,
        'Zap': Zap,
        'Car': Car,
        'Skull': Skull,
        'LogOut': LogOut,
        'MessageSquareWarning': MessageSquareWarning
      };

      // Mapear apenas categorias ATIVAS no banco
      const visibleOptions = categories
        .filter(cat => cat.is_active) // <--- O PULO DO GATO: Respeita o banco
        .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
        .map(cat => {
          // Tenta achar configurações visuais no config estático pelo slug (fallback)
          const staticConfig = rawStep.options.find(opt => opt.id === cat.slug);

          return {
            id: cat.slug,
            label: cat.label, // Usa label do banco (editável)
            desc: cat.description, // Usa descrição do banco (editável)
            icon: ICON_MAP[cat.icon] || (staticConfig ? staticConfig.icon : HelpCircle),
            colorClass: staticConfig ? staticConfig.colorClass : 'from-gray-500/20 to-gray-600/20 text-gray-400 group-hover:ring-gray-500/50'
          };
        });

      return {
        ...rawStep,
        options: visibleOptions
      };
    }

    // Fallback: Se não carregou categorias do banco ainda, mostra o padrão do Config
    return rawStep;
  }, [currentStepId, categories]);

  // Determine the active category for FAQ suggestions
  const activeFlowId = (() => {
    // Check current step first
    if (currentStep?.categoryMap) return currentStep.categoryMap;
    // Then check history
    for (let i = history.length - 1; i >= 0; i--) {
      const hId = history[i];
      if (FLOW_CONFIG[hId]?.categoryMap) {
        return FLOW_CONFIG[hId].categoryMap;
      }
    }
    return null;
  })();

  // Efeito para focar no input quando mudar de passo
  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [currentStepId]);

  // Fetch Cupom 70 Status
  useEffect(() => {
    const fetchCupomSettings = async () => {
      try {
        const { data } = await supabase.from('system_settings').select('value').eq('key', 'cupom70_status').single();
        if (data && data.value) {
          setCupomSettings(data.value);
        }
      } catch (err) {
        console.warn("Erro buscar configurações cupom", err);
      }
    };
    fetchCupomSettings();
    // Refresh a cada 1 minuto
    const interval = setInterval(fetchCupomSettings, 60000);
    return () => clearInterval(interval);
  }, []);

  // Ticker Cronômetro Cupom 70
  useEffect(() => {
    if (!cupomSettings || cupomSettings.status !== 'suspended_auto' || !cupomSettings.autoSuspendAt || !cupomSettings.showTimer) {
      setCupomTimeLeft('');
      return;
    }

    const interval = setInterval(() => {
      const diff = new Date(cupomSettings.autoSuspendAt).getTime() - Date.now();
      if (diff <= 0) {
        setCupomTimeLeft('ESGOTADO');
        clearInterval(interval);
      } else {
        const h = Math.floor(diff / (1000 * 60 * 60));
        const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const s = Math.floor((diff % (1000 * 60)) / 1000);
        setCupomTimeLeft(`${h}h ${m}m ${s}s`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [cupomSettings]);

  // Atualiza sugestões de FAQ quando a categoria muda
  useEffect(() => {
    if (activeFlowId && faqs) {
      const categoryFaqs = faqs.filter(f => f.category === activeFlowId);
      setSuggestedFaqs(categoryFaqs);
    } else {
      setSuggestedFaqs([]);
    }
  }, [activeFlowId, faqs]);

  // Atualiza sugestões de tickets similares quando a descrição muda
  const currentDescription = answers[currentStepId] || '';
  useEffect(() => {
    if (currentStep?.id === 'description' && currentDescription.length > 10 && getSimilarTickets) {
      const similar = getSimilarTickets(currentDescription);
      setSimilarTickets(similar);
    } else {
      setSimilarTickets([]);
    }
  }, [currentStepId, currentDescription, currentDescription.length, getSimilarTickets]);

  useEffect(() => {
    if (user?.discordId) {
      const punishments = getActivePunishments(user.discordId);
      const activeBan = punishments.find(p => p.punishmentType === 'Blacklist de Tickets');

      if (activeBan) {
        setIsBanned(true);
        setBanPunishment(activeBan);

        // Check if there is an existing appeal
        if (activeBan.appealTicketId) {
          setExistingAppealTicketId(activeBan.appealTicketId);

          // Need to find the ticket to check its status
          let appealTicket = null;
          if (Array.isArray(tickets)) {
            appealTicket = tickets.find(t => t && t.id === activeBan.appealTicketId);
          }


          if (appealTicket) {
            if (['fechado', 'resolvido'].includes(appealTicket.status)) {
              // Appeal ticket is closed but ban is still active -> Denied
              setAppealDenied(true);
            } else {
              // Appeal ticket is open -> Submitted/In Progress
              setAppealSubmitted(true);
            }
          } else {
            // If we have an ID but can't find the ticket (maybe not loaded), assume submitted for safety
            setAppealSubmitted(true);
          }
        }
      }
    }
  }, [user, getActivePunishments, tickets]);

  const handleBanAppeal = async () => {
    if (!appealText) return;
    setIsSubmitting(true);

    const payload = {
      title: `URGENTE: Recurso de Banimento de Tickets - ${user.name}`,
      description: `
[vermelho:🚨 Recurso de Bloqueio de Tickets]

**Usuário:** ${user.name} (ID: ${user.discordId})
**Motivo do Bloqueio:** ${banPunishment.publicReason}
**Data do Bloqueio:** ${new Date(banPunishment.appliedAt).toLocaleDateString('pt-BR')}

[azul:📝 Defesa do Usuário]
${appealText}

---
*Este ticket foi gerado automaticamente pelo sistema de recurso de banimento.*
        `,
      priority: 'urgent',
      status: 'open',
      category: 'recurso_banimento', // Special category
      department: 'head_staff', // Assign to Head Staff
      type: 'APPEAL_BAN',
      metadata: {
        banPunishmentId: banPunishment.id
      }
    };

    try {
      const result = await createTicket(payload);
      if (result.success) {
        setAppealSubmitted(true);
        toast.success('Recurso enviado com sucesso! A administração analisará em breve.');
      } else {
        toast.error('Erro ao enviar recurso. Tente novamente.');
      }
    } catch (error) {
      console.error("Error creating appeal ticket:", error);
      toast.error('Erro interno ao enviar recurso.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const bypassDuplicateWarning = () => {
    if (!pendingNextStepId) return;

    const value = pendingStepValue !== null ? pendingStepValue : answers[currentStepId];
    const nextId = pendingNextStepId;

    // Proceder funcionalmente para evitar race conditions
    setAnswers(prev => ({ ...prev, [currentStepId]: value }));
    setHistory(prev => [...prev, currentStepId]);
    setCurrentStepId(nextId);
    setDirection(1);

    // Limpar estados de aviso
    setShowDuplicateWarning(false);
    setDuplicateTicket(null);
    setPendingStepValue(null);
    setPendingNextStepId(null);
  };

  const renderDuplicateWarning = () => {
    if (!duplicateTicket) return null;
    return (
      <div className="flex flex-col items-center justify-center py-4 w-full animate-in fade-in zoom-in duration-300">
        <div className="bg-[#0f1115] border border-yellow-500/20 rounded-2xl md:rounded-3xl p-6 md:p-10 max-w-2xl w-full text-center relative overflow-hidden shadow-2xl flex flex-col items-center">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent opacity-50"></div>

          <div className="w-16 h-16 md:w-20 md:h-20 bg-yellow-500/10 rounded-full flex items-center justify-center mb-6 ring-1 ring-yellow-500/20 shadow-[0_0_30px_-5px_rgba(234,179,8,0.3)]">
            <AlertCircle className="w-8 h-8 md:w-10 md:h-10 text-yellow-500" />
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">Atenção: Ticket Duplicado</h1>

          <div className="space-y-4 mb-8 max-w-lg">
            <p className="text-gray-300 text-base leading-relaxed">
              Você já possui um ticket em aberto nesta categoria para este personagem.
              Criar um novo caso <strong>não vai agilizar</strong> o atendimento.
            </p>

            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-200 flex items-start gap-2 text-left">
              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
              <span>
                <strong>Aviso Importante:</strong> A insistência em abrir múltiplos tickets sobre o mesmo assunto é considerada <strong>spam</strong> e pode resultar em punição (advertência) na conta deste personagem.
              </span>
            </div>
          </div>

          <div
            onClick={() => navigate(`/tickets/${duplicateTicket.id}`)}
            className="w-full bg-white/5 hover:bg-white/10 active:scale-[0.98] cursor-pointer rounded-xl p-5 mb-8 border border-white/5 hover:border-yellow-500/30 text-left max-w-lg transition-all duration-200 group relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 translate-x-[-100%] group-hover:translate-x-[100%]"></div>

            <div className="flex justify-between items-start mb-2 relative z-10">
              <h3 className="text-white font-bold text-lg group-hover:text-yellow-400 transition-colors">{duplicateTicket.title}</h3>
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/20">
                Ver Ticket
              </Badge>
            </div>
            <p className="text-gray-400 text-sm line-clamp-2 mb-3 leading-relaxed">{duplicateTicket.description || 'Sem descrição'}</p>
            <div className="flex items-center justify-between text-xs text-gray-500 relative z-10">
              <div className="flex gap-4">
                <span className="flex items-center gap-1.5"><Clock size={12} /> {new Date(duplicateTicket.createdAt).toLocaleDateString()}</span>
                <span className="flex items-center gap-1.5"><Hash size={12} /> #{duplicateTicket.id}</span>
              </div>
              <div className="flex items-center gap-1 text-yellow-500/80 font-medium">
                Clique para visualizar <ArrowRight size={12} />
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full max-w-lg">
            <Button
              onClick={() => navigate(`/tickets/${duplicateTicket.id}`)}
              className="bg-yellow-600 hover:bg-yellow-700 text-white h-12 px-8 text-base font-bold shadow-lg shadow-yellow-900/20 flex-1"
            >
              Acompanhar Ticket Atual
            </Button>
            <Button
              onClick={bypassDuplicateWarning}
              variant="outline"
              className="border-white/10 text-gray-400 hover:text-white hover:bg-white/5 h-12 px-6 flex-1"
            >
              O assunto é diferente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Avançar para o próximo passo
  const handleNext = async (answerValue = null, forceBypass = false) => {
    // Se nenhum valor for passado, tenta pegar do state answers (ex: botão próximo)
    const value = answerValue !== null ? answerValue : answers[currentStepId];

    // Validação básica
    if (currentStep.required && !value && value !== 0 && value !== '0') {
      // Pequena animação de erro se necessário
      return;
    }

    // Salvar resposta
    const newAnswers = { ...answers, [currentStepId]: value };
    setAnswers(newAnswers);

    // Calcular próximo passo
    let nextStepId = 'finish';
    if (typeof currentStep.next === 'function') {
      nextStepId = currentStep.next(value);
    } else {
      nextStepId = currentStep.next;
    }

    // --- INTERCEPT: Check for Duplicate Tickets ---
    // Logica: Se o usuario acabou de selecionar a cidade (step type city_select)
    // Precisamos saber a CATEGORIA atual para verificar se já existe ticket
    if (currentStep.type === 'city_select' && value && !forceBypass) {
      const selectedCity = value;
      const cityData = user?.cities?.find(c => c.city === selectedCity);

      // Determinar a categoria baseada no step anterior ou atual
      // O FLOW_CONFIG não tem a categoria explicita no step 'city_select' pq ele é reusado
      // Mas o ID do step indica o fluxo (ex: city_select_denuncia)
      // Vamos tentar mapear pelo ID do step atual
      let flowCategory = null;
      if (currentStepId.includes('denuncia')) flowCategory = 'denuncia_jogador';
      else if (currentStepId.includes('compras')) flowCategory = 'compras_doacoes';
      else if (currentStepId.includes('tecnico')) flowCategory = 'programacao'; // Ou suporte_duvidas? Ajustado conforme FlowConfig: map['tecnico'] -> city_select_tecnico -> tecnico_tipo
      else if (currentStepId.includes('punicao')) flowCategory = 'contestar_punicao';
      else if (currentStepId.includes('staff')) flowCategory = 'denuncia_staff';
      else if (currentStepId.includes('duvidas')) flowCategory = 'suporte_duvidas';
      else if (currentStepId.includes('cupons')) flowCategory = 'cupons_descontos';

      // Special case: 'tecnico' redirects to 'tecnico_tipo', so we might not know the exact sub-category yet 
      // but usually 'tecnico' maps to 'programacao' or 'suporte_duvidas' depending on choices.
      // For now, let's treat 'city_select_tecnico' as potentially 'programacao' (bugs) for safety check.

      if (flowCategory && cityData && tickets) {
        const charId = cityData.playerId;

        // Find duplicate active ticket
        const existingTicket = tickets.find(t =>
          t.category === flowCategory &&
          (
            String(t.playerId) === String(charId) ||
            String(t.characterId) === String(charId) ||
            String(t.formData?.characterId) === String(charId)
          ) &&
          !['resolvido', 'fechado', 'cancelled', 'deleted'].includes(t.status)
        );

        if (existingTicket) {
          console.log("Duplicate ticket found:", existingTicket);
          setDuplicateTicket(existingTicket);
          setPendingStepValue(value);
          setPendingNextStepId(nextStepId); // Salva o destino
          setShowDuplicateWarning(true);
          return; // Stop flow
        }
      }
    }

    // --- INTERCEPT: Ticket de Punição ---
    if (currentStepId === 'city_select_punicao' && value) {
      // O value aqui é o nome da cidade. Vamos achar o usuário.
      const cityData = user?.cities?.find(c => c.city === value);
      if (cityData) {
        // Buscar todas as punições ativas ou em análise para este personagem
        const punis = getActiveCharacterPunishments(cityData.playerId);

        // Mapear punições e identificar as que já possuem recurso
        const processedPunis = punis?.map(p => {
          // Já tem link direto?
          if (p.appealTicketId) return { ...p, isContested: true, existingTicketId: p.appealTicketId };

          // Retroactive lookup
          // Verifica se existe algum ticket de APELAÇÃO aberto para esta punição
          const existingTicket = tickets?.find(t =>
            t.type === 'APPEAL' &&
            t.metadata?.punishmentId === p.id &&
            !['cancelled', 'deleted'].includes(t.status) // Ignora cancelados/deletados
          );

          if (existingTicket) {
            return { ...p, isContested: true, existingTicketId: existingTicket.id };
          }

          return { ...p, isContested: false };
        });

        // Se tiver qualquer punição (mesmo já contestada), desvia o fluxo para mostrar a lista
        if (processedPunis && processedPunis.length > 0) {
          console.log("Punições encontradas:", processedPunis);
          setFoundPunishments(processedPunis);

          setHistory([...history, currentStepId]);
          setDirection(1);
          setCurrentStepId('punishment_selection_intercept');
          return;
        }
      }
    }

    if (nextStepId === 'super_cupom_auto_create') {
      const finalAnswers = { ...answers, [currentStepId]: value };
      setAnswers(finalAnswers);
      setHistory([...history, currentStepId]);
      setCurrentStepId(nextStepId);
      handleSubmit(finalAnswers, nextStepId);
      return;
    }

    if (nextStepId === 'finish') {
      setHistory([...history, currentStepId]); // Add current step to history so we can find it later
      setCurrentStepId('review');
    } else {
      setHistory([...history, currentStepId]);
      setDirection(1);
      setCurrentStepId(nextStepId);
    }
  };

  // Voltar passo
  const handleBack = () => {
    if (history.length === 0) {
      if (currentStepId === 'review') {
        // If backing out of review, go to the last step in history
        const lastStep = history[history.length - 1]; // Actually we need to reconstruct where we came from.
        // Better strategy: The review step behaves like a normal step in our flow logic now
        // So we just pop history as usual if we treat 'review' as a step that was pushed?
        // No, 'review' is special. Let's handle it explicitly.
      }
      navigate('/tickets');
      return;
    }

    if (currentStepId === 'review') {
      const prevStepId = history[history.length - 1];
      // Don't pop history here because we want to be able to come back to review?
      // Actually standard back behavior is fine: pop history and go there.
      const newHistory = history.slice(0, -1);
      setHistory(newHistory);
      setDirection(-1);
      setCurrentStepId(prevStepId);
      return;
    }

    const prevStepId = history[history.length - 1];
    const newHistory = history.slice(0, -1);

    setHistory(newHistory);
    setDirection(-1);
    setCurrentStepId(prevStepId);
  };

  // Envio final REAL
  const confirmSubmit = () => {
    // Find the last step before 'review' to use as 'lastStepId' for category mapping
    // logic. In our history, the last item is the last step visited.
    const lastActionStepId = history[history.length - 1];
    handleSubmit(answers, lastActionStepId);
  };

  // Envio final logic
  const handleSubmit = async (finalAnswers, lastStepId) => {
    setIsSubmitting(true);

    const payload = {
      id: pendingTicketId,
      title: `Ticket - ${user.name}`,
      priority: 'normal',
      status: 'aguardando_sistema',
    };


    // Helper para converter arquivo em Base64
    const fileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    };

    // Percorrer todas as respostas
    // MODIFICADO: Loop 'for' para suportar await na conversão de imagem
    for (const stepId of Object.keys(finalAnswers)) {
      const stepConfig = FLOW_CONFIG[stepId];
      if (stepConfig && stepConfig.resultKey) {
        let val = finalAnswers[stepId];

        // Se for upload, converter para Base64
        if (stepConfig.type === 'upload' && Array.isArray(val)) {
          const filesWithBase64 = await Promise.all(val.map(async (f) => {
            try {
              // Se já for string (caso de edição futura), ignora
              if (typeof f === 'string') return f;

              // Converte File object para Base64
              const base64 = await fileToBase64(f);
              // Como o backend simula receber URL, vamos mandar o Base64 completo
              // O TicketViewUser já sabe lidar com isso se for detectado como Base64 ou URL
              return base64;
            } catch (err) {
              console.error("Erro ao converter arquivo:", err);
              return f.name; // Fallback
            }
          }));
          val = filesWithBase64;
        }

        payload[stepConfig.resultKey] = val ?? null;
      }


      // Tratamento especial para cidade
      if (stepConfig && stepConfig.type === 'city_select') {
        const cityValue = finalAnswers[stepId];
        payload.city = cityValue;
        if (user?.cities) {
          const matchedCity = user.cities.find(c => c.city === cityValue);
          if (matchedCity) {
            payload.playerId = matchedCity.playerId; // Correct field for TicketContext
            payload.characterId = matchedCity.playerId; // Keep for consistency/formData
            payload.characterName = matchedCity.characterName;
          }
        }
      }
    }

    // --- LOGICA DE RECURSO (Override) ---
    if (selectedPunishment && finalAnswers['punishment_appeal_form']) {
      const appealData = finalAnswers['punishment_appeal_form'];

      // Formatar descrição rica
      const richDescription = [
        `[vermelho:🚨 Recurso de Punição]`,
        ``,
        `**👤 Personagem:** ${selectedPunishment.characterName || payload.characterName}`,
        `**🆔 ID do Personagem:** ${selectedPunishment.characterId || payload.characterId || 'N/A'}`,
        `**🆔 ID da Punição:** ${selectedPunishment.id}`,
        `**📅 Data da Aplicação:** ${new Date(selectedPunishment.appliedAt).toLocaleDateString('pt-BR')}`,
        `**🚫 Tipo:** ${selectedPunishment.punishmentType}`,
        ``,
        `[azul:📝 Contestação do Usuário]`,
        `${appealData.defense}`,
        ``,
        `[laranja:📹 Provas / Detalhes Adicionais]`,
        `**Vídeo:** ${appealData.video || 'Nenhum vídeo anexado'}`,
        `**Minuto/Momento:** ${appealData.time || 'Não informado'}`,
        ``,
        `---`,
        `*Ticket gerado via Fluxo de Criação*`
      ].join('\n');

      payload.description = richDescription;
      payload.title = `RECURSO: ${selectedPunishment.characterName} - ${selectedPunishment.punishmentType} [#${selectedPunishment.id}]`;
      payload.category = 'Recurso'; // Força categoria
      payload.type = 'APPEAL';
      payload.metadata = {
        punishmentId: selectedPunishment.id,
        originalPunishment: selectedPunishment
      };
    }

    // Definir Categoria e Departamento (se não for recurso já definido)
    if (!payload.category) {
      let category = '';
      if (FLOW_CONFIG[lastStepId]?.categoryMap) {
        category = FLOW_CONFIG[lastStepId].categoryMap;
      } else {
        for (let i = history.length - 1; i >= 0; i--) {
          const hId = history[i];
          if (FLOW_CONFIG[hId]?.categoryMap) {
            category = FLOW_CONFIG[hId].categoryMap;
            break;
          }
        }
      }

      if (category) {
        payload.category = category;
        const cityStr = payload.city ? payload.city.toUpperCase() : 'GERAL';
        const charStr = payload.characterName ? payload.characterName : user.name;
        const catStr = category.toUpperCase().replace('_', ' ');
        payload.title = `TICKET: ${cityStr} - ${charStr} [${catStr}]`;

        const deptMap = {
          'suporte_duvidas': 'suporte',
          'denuncia_jogador': 'moderacao',
          'compras_doacoes': 'suporte',
          'contestar_punicao': 'moderacao',
          'cupons_descontos': 'suporte',
          'denuncia_staff': 'admin',
          'programacao': 'programacao'
        };
        payload.department = deptMap[category] || 'suporte';
      }
    }

    // --- ANTI-SPAM CHECK ---
    // Regra: Se o usuário criar +3 tickets dentro de 24 horas, vai para Triagem (status: verificacao)
    // E recebe aviso do bot.
    if (Array.isArray(tickets) && user) {
      const ONE_DAY_MS = 24 * 60 * 60 * 1000;
      const recentTickets = tickets.filter(t => {
        if (!t || typeof t !== 'object') return false;

        const ticketAuthorId = String(t.userId || '');
        const ticketDiscordId = String(t.userDiscordId || '');
        const userDiscordId = String(user.discordId || '');
        const userId = String(user.id || '');

        const isMatch = ticketAuthorId === userId ||
          ticketDiscordId === userDiscordId ||
          ticketAuthorId === userDiscordId;

        // Safety check for createdAt
        const createdAtTime = t.createdAt ? new Date(t.createdAt).getTime() : 0;
        const isRecent = t.createdAt && !isNaN(createdAtTime) && (createdAtTime > Date.now() - ONE_DAY_MS);

        const isActive = !['cancelled', 'deleted'].includes(t.status);

        return isMatch && isRecent && isActive;
      });


      if (recentTickets.length >= 3) {
        console.log(`[Anti-Spam] User ${user.name} has ${recentTickets.length} active/recent tickets. Triggering Lock.`);

        // 1. Forçar status para Triagem
        payload.status = 'verificacao';

        // 2. Adicionar flag auxiliar se necessário
        if (!payload.auxiliaryStatus) payload.auxiliaryStatus = [];
        payload.auxiliaryStatus.push('spam_hold');

        // 3. Adicionar resposta automática do Bot
        const botResponse = {
          id: Date.now(),
          message: `🔒 **SISTEMA DE SEGURANÇA**\n\nOlá **${user.name}**,\n\nIdentificamos que você criou muitos tickets num curto período de tempo.\n\nPor questões de segurança e para manter a organização do nosso atendimento, este chamado foi movido automaticamente para a **Triagem de Segurança**.\n\n⚠️ **O que isso significa?**\nEste ticket permanecerá com status restrito e só será liberado para a fila de atendimento normal após **24 horas** de sua criação.\n\nAgradecemos a compreensão.`,
          isInternal: false,
          createdAt: new Date().toISOString(),
          userId: 'system_bot_security',
          userName: 'Sistema de Segurança',
          author: 'Sistema de Segurança',
          authorRole: 'system',
          isStaff: true,
          isBot: true,
          authorAvatar: 'shield_lock' // Pode ser tratado no frontend para ícone específico
        };

        if (!payload.responses) payload.responses = [];
        payload.responses.push(botResponse);
      }
    }

    try {
      const result = await createTicket(payload);

      // Se for recurso, vincula ao sistema de punição
      if (result.success && selectedPunishment && finalAnswers['punishment_appeal_form']) {
        await createAppeal(selectedPunishment.id, finalAnswers['punishment_appeal_form'].defense, result.ticketId);
      }

      if (result.success && result.ticketId) {
        // Deterministic Navigation: Ticket is already in Context state.
        navigate(`/tickets/${result.ticketId}`);
      } else {
        const errorMsg = result.success
          ? 'Ticket criado mas ID não retornado.'
          : ('Erro ao criar ticket: ' + (result.error || 'Erro desconhecido'));

        console.error(errorMsg);
        alert(errorMsg);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error(error);
      alert('Erro interno');
      setIsSubmitting(false);
    }
  };

  // Renderizadores de Input
  const renderInput = () => {
    switch (currentStep.type) {
      case 'choice':
        return (
          <div className="flex flex-wrap justify-center gap-4 mt-8 w-full max-w-6xl mx-auto px-4">
            {currentStep.options.map((opt) => {
              const Icon = opt.icon;

              // Lógica dinâmica de restrição para a Categoria Cupom 70
              const isCupom = opt.id === 'super_cupom' || opt.id === 'cupom_70';
              let isSuspended = false;
              let showAutoTimer = false;
              let isSoldOutLabel = false;

              if (isCupom && cupomSettings) {
                if (cupomSettings.status === 'suspended_manual') {
                  isSuspended = true;
                  isSoldOutLabel = true;
                } else if (cupomSettings.status === 'suspended_auto' && cupomSettings.autoSuspendAt) {
                  const diff = new Date(cupomSettings.autoSuspendAt).getTime() - Date.now();
                  if (diff <= 0) {
                    isSuspended = true;
                    isSoldOutLabel = true;
                  } else {
                    showAutoTimer = cupomSettings.showTimer;
                  }
                }
              }

              return (
                <button
                  key={opt.id}
                  onClick={() => !isSuspended && handleNext(opt.id)}
                  disabled={isSuspended}
                  className={`relative group p-5 rounded-2xl bg-white/5 border border-white/10 transition-all duration-300 overflow-hidden w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.33%-1rem)] text-left flex flex-col items-start gap-4 ${isSuspended
                    ? 'opacity-50 cursor-not-allowed grayscale border-red-500/20 pb-8'
                    : 'hover:border-blue-500/50 hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-500/10'
                    }`}
                >
                  <div className="flex items-center gap-5 w-full relative z-10">
                    {/* Icon Container */}
                    <div className={`relative flex-shrink-0 p-4 rounded-xl bg-gradient-to-br ${opt.colorClass || 'from-blue-500/20 to-purple-600/20 text-blue-400 group-hover:ring-blue-500/50'} transition-all duration-300 ring-1 ring-white/10 ${!isSuspended && 'group-hover:scale-110'}`}>
                      {Icon && <Icon size={24} strokeWidth={1.5} />}
                    </div>

                    {/* Text Content */}
                    <div className="relative z-10 flex-1">
                      <h3 className={`text-lg font-bold text-white transition-colors ${!isSuspended && 'group-hover:text-blue-200'}`}>
                        {opt.label}
                      </h3>
                      {opt.desc && (
                        <p className={`text-xs text-gray-400 leading-tight mt-1 ${!isSuspended && 'group-hover:text-gray-300'}`}>
                          {opt.desc}
                        </p>
                      )}
                    </div>

                    {/* Arrow Indicator */}
                    {!isSuspended && (
                      <div className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-blue-400 mr-2 flex-shrink-0">
                        <ArrowRight className="w-5 h-5" />
                      </div>
                    )}
                  </div>

                  {/* Cupom UI Elements: Extendido */}
                  {isSuspended && isSoldOutLabel && (
                    <div className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center overflow-hidden">
                      {/* Faixa 1 (Diagonal principal) */}
                      <div className="absolute w-[140%] h-7 bg-red-600/90 border-y border-red-400/50 -rotate-[25deg] shadow-xl flex items-center justify-center">
                        <span className="text-[10px] font-black text-white tracking-[0.2em] uppercase whitespace-nowrap px-4 drop-shadow-md">
                          Aguarde Próxima Promoção
                        </span>
                      </div>
                      {/* Faixa 2 (Diagonal secundária) */}
                      <div className="absolute w-[140%] h-7 bg-red-800/80 border-y border-red-500/50 rotate-[25deg] shadow-xl flex items-center justify-center">
                        <span className="text-[10px] font-black text-white tracking-[0.2em] uppercase whitespace-nowrap px-4 drop-shadow-md">
                          Aguarde Próxima Promoção
                        </span>
                      </div>
                    </div>
                  )}

                  {!isSuspended && showAutoTimer && cupomTimeLeft && (
                    <div className="w-full mt-2 flex items-center justify-center gap-2 bg-yellow-500/10 border border-yellow-500/30 py-2 rounded-lg relative z-20 shadow-inner">
                      <Clock className="w-4 h-4 text-yellow-500 animate-pulse" />
                      <span className="text-xs font-bold text-yellow-400 tracking-widest tabular-nums cursor-default">
                        ACABA EM: {cupomTimeLeft}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        );

      case 'text':
      case 'email':
      case 'number':
        return (
          <div className="mt-6 w-full max-w-2xl px-4">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl opacity-20 group-focus-within:opacity-100 transition duration-300 blur-sm"></div>
              <div className="relative flex items-center bg-gray-900 border border-white/10 rounded-xl overflow-hidden px-6 py-4">
                <Input
                  ref={inputRef}
                  type={currentStep.type === 'email' ? 'email' : currentStep.type === 'number' ? 'number' : 'text'}
                  placeholder={currentStep.placeholder}
                  value={answers[currentStepId] || ''}
                  onChange={(e) => setAnswers({ ...answers, [currentStepId]: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && handleNext()}
                  className="bg-transparent border-0 text-white text-2xl h-12 focus-visible:ring-0 placeholder:text-gray-600 font-medium w-full"
                />
              </div>
            </div>
            <div className="mt-8 flex gap-3">
              <Button onClick={() => handleNext()} disabled={!answers[currentStepId] && currentStep.required} className="bg-blue-600 hover:bg-blue-700 text-white px-8 h-12 text-lg font-medium shadow-lg hover:shadow-blue-500/20 transition-all">
                Continuar <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              {currentStep.required === false && (
                <Button variant="ghost" onClick={() => handleNext('')} className="h-12 text-gray-400 hover:text-white">
                  Pular
                </Button>
              )}
            </div>

            {/* Sugestões de Tickets Similares */}
            {currentStep.id === 'description' && similarTickets.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 w-full max-w-2xl bg-blue-500/10 border border-blue-500/20 rounded-xl p-4"
              >
                <div className="flex items-center gap-2 mb-3 text-blue-400">
                  <AlertCircle size={18} />
                  <span className="font-semibold text-sm">Você já abriu tickets similares:</span>
                </div>
                <div className="space-y-2">
                  {similarTickets.map(ticket => (
                    <div
                      key={ticket.id}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/tickets/${ticket.id}`);
                      }}
                      className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors group cursor-pointer"
                    >
                      <div className="flex flex-col">
                        <span className="text-white text-sm font-medium">{ticket.title}</span>
                        <span className="text-xs text-gray-500">Status: {ticket.status}</span>
                      </div>
                      <ArrowRight size={14} className="text-gray-500 group-hover:text-white transition-colors" />
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        );

      case 'upload':
        return (
          <div className="mt-8 w-full max-w-2xl px-4 flex flex-col items-center">
            <R2Upload
              onUploadComplete={(urls) => setAnswers({ ...answers, [currentStepId]: urls })}
              ticketContext={{
                ticketId: pendingTicketId,
                userId: user.discordId,
                city: answers['city_select_denuncia'] || answers['city_select_compras'] || answers['city_select_tecnico'] || answers['city_select_punicao'] || answers['city_select_staff'] || answers['city_select_duvidas'] || answers['city_select_cupons'] || 'geral'
              }}
            />
            <div className="mt-8 flex gap-3 w-full justify-center">
              <Button
                onClick={() => handleNext()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 h-12 text-lg font-medium shadow-lg hover:shadow-blue-500/20 transition-all"
              >
                {answers[currentStepId]?.length > 0 ? 'Continuar com Arquivos' : 'Pular / Continuar'} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        );

      case 'textarea':
        return (
          <div className="mt-6 w-full max-w-2xl">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl opacity-20 group-focus-within:opacity-100 transition duration-300 blur-sm"></div>
              <Textarea
                ref={inputRef}
                placeholder={currentStep.placeholder}
                value={answers[currentStepId] || ''}
                onChange={(e) => setAnswers({ ...answers, [currentStepId]: e.target.value })}
                className="relative bg-gray-900 border-white/10 text-white min-h-[250px] p-6 text-xl leading-relaxed resize-none focus-visible:ring-0 rounded-xl placeholder:text-gray-600 shadow-xl"
              />
            </div>
            <div className="mt-6 flex gap-3">
              <Button onClick={() => handleNext()} disabled={!answers[currentStepId] && currentStep.required} className="bg-blue-600 hover:bg-blue-700 text-white px-8 h-12 text-lg font-medium shadow-lg hover:shadow-blue-500/20 transition-all">
                {FLOW_CONFIG[currentStepId].categoryMap ? 'Revisar Ticket' : 'Continuar'} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        );

      case 'date':
        return (
          <div className="mt-6 w-full max-w-2xl px-4">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl opacity-20 group-focus-within:opacity-100 transition duration-300 blur-sm"></div>
              <div className="relative flex items-center bg-gray-900 border border-white/10 rounded-xl overflow-hidden px-6 py-4">
                <Input
                  ref={inputRef}
                  type="date"
                  value={answers[currentStepId] || ''}
                  onChange={(e) => setAnswers({ ...answers, [currentStepId]: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && handleNext()}
                  className="bg-transparent border-0 text-white text-2xl h-12 focus-visible:ring-0 w-full dark:[color-scheme:dark]"
                />
              </div>
            </div>
            <div className="mt-8">
              <Button onClick={() => handleNext()} disabled={!answers[currentStepId] && currentStep.required} className="bg-blue-600 hover:bg-blue-700 text-white h-12 px-8 text-lg font-medium shadow-lg hover:shadow-blue-500/20 transition-all">
                Continuar <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        );

      case 'city_select':
        const availableCities = user?.cities?.filter(c => !['pending_approval', 'pending'].includes(c.status)) || [];

        return (
          <div className="mt-6 flex justify-center w-full px-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
              {availableCities.length > 0 ? (
                availableCities.map((cityData) => (
                  <button
                    key={cityData.city}
                    onClick={() => handleNext(cityData.city)}
                    className="flex flex-col p-5 bg-white/5 hover:bg-blue-500/20 border border-white/10 hover:border-blue-500/50 rounded-xl transition-all text-left group"
                  >
                    <span className="text-xl font-bold text-white mb-1 group-hover:text-blue-400 transition-colors">{cityData.city}</span>
                    <span className="text-sm text-gray-400 group-hover:text-gray-300">{cityData.characterName} (ID: {cityData.playerId})</span>
                  </button>
                ))
              ) : (
                <div className="col-span-full flex flex-col items-center justify-center p-8 text-center bg-white/5 rounded-xl border border-dashed border-white/10">
                  <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-4">
                    <MapPin className="w-8 h-8 text-blue-400" />
                  </div>
                  <p className="text-gray-300 font-medium mb-2">Nenhuma cidade disponível</p>
                  <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6">
                    Se você acabou de vincular uma cidade, ela pode estar em análise pela nossa equipe. Aguarde a aprovação.
                  </p>
                  <Button
                    variant="outline"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    onClick={logout}
                  >
                    Sair e Sincronizar
                  </Button>
                </div>
              )}
            </div>
          </div >
        );

      case 'punishment_list':
        return (
          <div className="flex flex-col items-center w-full max-w-4xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full mb-8">
              {foundPunishments.map((punishment) => (
                <button
                  key={punishment.id}
                  disabled={punishment.isContested}
                  onClick={() => {
                    if (punishment.isContested) return;
                    setSelectedPunishment(punishment);
                    handleNext(punishment.id);
                  }}
                  className={`relative group bg-[#0f1115] border rounded-xl p-5 text-left transition-all shadow-lg 
                    ${punishment.isContested
                      ? 'border-gray-500/20 opacity-70 cursor-not-allowed'
                      : 'border-red-500/20 hover:border-red-500/50 hover:bg-white/5 shadow-red-900/10'
                    }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className={`p-2 rounded-lg ${punishment.isContested ? 'bg-gray-500/10 text-gray-400' : 'bg-red-500/10 text-red-400'}`}>
                      {punishment.isContested ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`text-xs font-bold px-2 py-1 rounded border ${punishment.isContested ? 'text-gray-400 bg-gray-500/10 border-gray-500/20' : 'text-red-500 bg-red-500/10 border-red-500/20'}`}>
                        #{punishment.id}
                      </span>
                      {punishment.isContested && (
                        <span className="text-[10px] font-bold text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/20 uppercase">
                          Já Contestado
                        </span>
                      )}
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1 group-hover:text-red-400 transition-colors">
                    {punishment.punishmentType}
                  </h3>
                  <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                    {punishment.publicReason}
                  </p>
                  <div className="flex items-center justify-between mt-auto">
                    <div className="flex items-center gap-2 text-xs text-gray-500 font-mono bg-black/20 p-2 rounded">
                      <Clock className="w-3 h-3" />
                      Expira: {punishment.expiresAt ? formatPunishmentDuration(punishment) : 'Indeterminado'}
                    </div>
                    {punishment.isContested && punishment.existingTicketId && (
                      <Button
                        variant="link"
                        size="sm"
                        className="text-blue-400 p-0 h-auto text-xs hover:text-blue-300"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tickets/${punishment.existingTicketId}`);
                        }}
                      >
                        Ver Ticket
                      </Button>
                    )}
                  </div>
                </button>
              ))}
            </div>

            <Button
              variant="ghost"
              onClick={() => {
                setHistory([...history, currentStepId]);
                setCurrentStepId('punicao_id'); // Fallback manual
              }}
              className="text-gray-400 hover:text-white"
            >
              Minha punição não está aqui
            </Button>
          </div>
        );

      case 'appeal_form':
        const appealData = answers[currentStepId] || { defense: '', video: '', time: '' };
        const updateAppeal = (field, val) => {
          setAnswers({
            ...answers,
            [currentStepId]: { ...appealData, [field]: val }
          });
        };

        return (
          <div className="w-full max-w-2xl space-y-6">

            {/* Warning Card */}
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 flex gap-4 text-left">
              <div className="p-2 bg-yellow-500/20 rounded-lg h-fit">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-yellow-500 uppercase tracking-widest mb-1">Atenção</h4>
                <p className="text-sm text-yellow-200/80 leading-relaxed">
                  Este é um processo único. Após enviar sua contestação, ela entrará em <strong>Processo de Análise</strong> e você não poderá contestar esta punição novamente. Certifique-se de apresentar todas as provas agora.
                </p>
              </div>
            </div>

            {selectedPunishment && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-5 flex flex-col gap-4 text-left">
                <div className="flex items-center gap-4 border-b border-red-500/10 pb-4">
                  <div className="p-3 bg-red-500/20 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-red-300 uppercase tracking-wider">Contestando Punição #{selectedPunishment.id}</h4>
                    <p className="text-white font-medium text-lg">{selectedPunishment.punishmentType}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-black/20 rounded-lg p-3 border border-red-500/10">
                    <p className="text-xs text-red-400/70 font-bold uppercase mb-1">Motivo</p>
                    <p className="text-sm text-gray-300">{selectedPunishment.publicReason}</p>
                  </div>
                  <div className="bg-black/20 rounded-lg p-3 border border-red-500/10">
                    <p className="text-xs text-red-400/70 font-bold uppercase mb-1">Expira em</p>
                    <p className="text-sm text-gray-300 flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      {selectedPunishment.expiresAt ? formatPunishmentDuration(selectedPunishment) : 'Indeterminado'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4 text-left">
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-300">Sua Defesa <span className="text-red-400">*</span></label>
                <Textarea
                  placeholder="Explique detalhadamente por que a punição foi injusta..."
                  className="bg-gray-900/50 border-white/10 min-h-[150px] text-white focus:border-red-500/50"
                  value={appealData.defense}
                  onChange={(e) => updateAppeal('defense', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-300 flex items-center gap-2"><Video className="w-3 h-3" /> Link do Vídeo (Opcional)</label>
                  <Input
                    placeholder="https://youtube.com/..."
                    className="bg-gray-900/50 border-white/10 text-white"
                    value={appealData.video}
                    onChange={(e) => updateAppeal('video', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-300 flex items-center gap-2"><Clock className="w-3 h-3" /> Minuto do Ocorrido</label>
                  <Input
                    placeholder="Ex: 10:45"
                    className="bg-gray-900/50 border-white/10 text-white"
                    value={appealData.time}
                    onChange={(e) => updateAppeal('time', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="pt-6">
              <Button
                onClick={() => handleNext(appealData)}
                disabled={!appealData.defense}
                className="w-full bg-red-600 hover:bg-red-700 text-white h-12 text-lg font-bold shadow-lg shadow-red-900/20"
              >
                Revisar e Enviar <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        );

      default:
        return <div className="text-red-500">Tipo de input desconhecido: {currentStep.type}</div>;
    }
  };

  // Renderização do Passo de Revisão (RESUMO)
  const renderReviewStep = () => {
    // Coleta dados para exibição
    const reviewData = Object.keys(answers).map(stepId => {
      const step = FLOW_CONFIG[stepId];
      if (!step || !step.question) return null;

      // Handle custom object values (like appeal form)
      if (answers[stepId] && typeof answers[stepId] === 'object') {
        if (stepId === 'punishment_appeal_form') {
          return { label: step.question, value: 'Formulário Preenchido' };
        }
        // Fallback for other objects to avoid crash
        return { label: step.question, value: '[Dados Complexos]' };
      }

      // Tenta achar o label da opção escolhida se for do tipo 'choice'
      let displayValue = answers[stepId];
      if (step.type === 'choice' && step.options) {
        const selectedOption = step.options.find(opt => opt.id === answers[stepId]);
        if (selectedOption) displayValue = selectedOption.label;
      }
      // Se for required false e vazio
      if (!displayValue) displayValue = "Não informado";

      return { label: step.question, value: displayValue };
    }).filter(Boolean);

    // Get character info explicitly if present in payload construction logic logic
    let characterInfo = null;
    const cityStepId = Object.keys(answers).find(key => FLOW_CONFIG[key]?.type === 'city_select');
    if (cityStepId && user?.cities) {
      const cityValue = answers[cityStepId];
      const cityData = user.cities.find(c => c.city === cityValue);
      if (cityData) characterInfo = `${cityData.characterName} (ID: ${cityData.playerId})`;
    }

    return (
      <div className="w-full max-w-2xl mx-auto px-4">
        <h2 className="text-2xl font-bold mb-6 text-center">Resumo do Ticket</h2>

        <div className="bg-gray-900 border border-white/10 rounded-2xl overflow-hidden backdrop-blur-md shadow-2xl">
          {/* Header do Resumo */}
          <div className="bg-gradient-to-r from-blue-600/10 to-purple-600/10 p-6 border-b border-white/10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-gray-400 text-sm font-medium uppercase tracking-wider">Você está criando um ticket de</p>
                <h3 className="text-xl font-bold text-white leading-tight">
                  {FLOW_CONFIG[answers['start']]?.options.find(o => o.id === answers['start'])?.label || 'Novo Ticket'}
                </h3>
              </div>
            </div>
          </div>

          {/* Conteúdo do Resumo */}
          <div className="p-6 space-y-6">
            {characterInfo && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-400">
                  <span className="font-bold text-xs">ID</span>
                </div>
                <div>
                  <p className="text-xs text-blue-300 uppercase font-bold">Personagem Selecionado</p>
                  <p className="text-white font-medium">{characterInfo}</p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {reviewData.map((item, index) => (
                <div key={index} className="group">
                  <p className="text-gray-500 text-sm font-medium mb-1 group-hover:text-blue-400 transition-colors">
                    {item.label}
                  </p>
                  <div className="text-gray-200 bg-black/20 rounded-lg p-3 border border-white/5 font-light">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer com Ação */}
          <div className="p-6 bg-black/40 border-t border-white/10 flex gap-3">
            <Button
              variant="outline"
              className="flex-1 border-white/10 hover:border-blue-500/50 bg-gradient-to-r from-blue-600/20 to-purple-600/20 hover:from-blue-600/40 hover:to-purple-600/40 text-gray-300 hover:text-white transition-all duration-300"
              onClick={() => handleBack()}
            >
              Voltar e Editar
            </Button>
            <Button
              className="flex-[2] bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold shadow-lg shadow-green-500/20 border-0"
              onClick={confirmSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Criando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Confirmar e Abrir Ticket <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Animação de transição
  const pageVariants = {
    initial: (dir) => ({ opacity: 0, y: dir > 0 ? 50 : -50 }),
    animate: { opacity: 1, y: 0 },
    exit: (dir) => ({ opacity: 0, y: dir > 0 ? -50 : 50 })
  };

  // Renderização do Passo Atual
  const renderStepContent = () => {
    return (
      <div className="w-full flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          {showDuplicateWarning ? (
            <h1 className="text-4xl font-bold text-center mb-4">
              <span className="text-yellow-500">Atenção Necessária</span>
            </h1>
          ) : (
            <h1 className="text-4xl font-bold text-center mb-4">
              <span className={`bg-gradient-to-r ${dynamicGradient} bg-[length:200%_auto] bg-clip-text text-transparent animate-gradient-x`}>
                {currentStep.question}
              </span>
            </h1>
          )}

          {currentStep.description && !showDuplicateWarning && (
            <p className="text-gray-400 text-lg md:text-xl text-center mb-10 max-w-2xl font-light">
              {currentStep.description}
            </p>
          )}
        </motion.div>
        {showDuplicateWarning ? renderDuplicateWarning() : renderInput()}

        {/* Sugestões de FAQ baseadas na categoria selecionada */}
        {suggestedFaqs.length > 0 && currentStep.type !== 'choice' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-12 w-full max-w-4xl px-4"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-blue-500/30"></div>
              <h3 className="text-blue-400 font-bold flex items-center gap-2 text-sm uppercase tracking-wider">
                <Hash size={16} /> Dicas Rápidas
              </h3>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-blue-500/30"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {suggestedFaqs.map((faq) => (
                <div
                  key={faq.id}
                  className="p-5 rounded-2xl bg-white/5 border border-white/10 hover:border-blue-500/30 transition-all group"
                >
                  <h4 className="text-white font-bold mb-2 group-hover:text-blue-400 transition-colors">
                    {faq.question}
                  </h4>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    );
  };

  // Main Render Logic
  if (isBanned) {
    if (appealDenied) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[70vh] p-3 md:p-4 animate-in fade-in zoom-in duration-300">
          <div className="bg-red-950/20 border border-red-500/10 rounded-2xl md:rounded-3xl p-6 md:p-10 max-w-3xl w-full text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-50"></div>

            <div className="w-16 h-16 md:w-24 md:h-24 bg-red-500/5 rounded-full flex items-center justify-center mx-auto mb-6 md:mb-8 ring-1 ring-red-500/20">
              <Gavel className="w-8 h-8 md:w-10 md:h-10 text-red-500 opacity-80" />
            </div>

            <h1 className="text-2xl md:text-4xl font-black text-white mb-4 md:mb-6 tracking-tight">Decisão Administrativa Mantida</h1>

            <div className="text-left bg-black/40 rounded-xl p-6 border border-white/5 mb-8 space-y-4">
              <p className="text-gray-300 leading-relaxed">
                Após análise detalhada do pedido de reconsideração protocolado sob o número <span className="text-white font-mono font-bold">#{existingAppealTicketId}</span>, a Administração, no exercício de suas atribuições regimentais, decidiu por <span className="text-red-400 font-bold">MANTER INTEGRALMENTE</span> a sanção administrativa originalmente aplicada.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Verificou-se que os argumentos apresentados na defesa <span className="text-red-400 font-semibold">não foram suficientes</span> para reformar a decisão inicial, permanecendo inalteradas as razões de fato e de direito que fundamentaram a aplicação da penalidade.
              </p>
              <p className="text-gray-400 leading-relaxed text-sm">
                Esta decisão é <span className="text-white font-bold">definitiva e irrecorrível</span> na esfera administrativa, esgotando-se as vias recursais disponíveis. Não será admitida a interposição de novo recurso sobre a mesma matéria.
              </p>
            </div>

            <div className="bg-black/40 rounded-xl p-6 max-w-xl mx-auto border border-white/5 mb-10">
              <p className="text-sm text-gray-500 uppercase tracking-widest font-bold mb-2">Protocolo do Processo Administrativo</p>
              <p className="text-white font-mono text-lg">#{existingAppealTicketId}</p>
            </div>

            <Button
              onClick={() => navigate('/tickets')}
              className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600 text-white h-12 px-10 text-base font-bold shadow-lg transition-all hover:scale-[1.02]"
            >
              Voltar para o Início
            </Button>
          </div>
        </div>
      );
    }

    if (appealSubmitted) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4 md:p-6 animate-in fade-in zoom-in duration-500">
          <div className="w-16 h-16 md:w-24 md:h-24 bg-emerald-500/10 rounded-full flex items-center justify-center mb-4 md:mb-6 ring-4 ring-emerald-500/20">
            <CheckCircle className="w-8 h-8 md:w-12 md:h-12 text-emerald-500" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Recurso Enviado</h2>
          <p className="text-gray-400 max-w-md mx-auto mb-6 md:mb-8 text-base md:text-lg px-4">
            Seu pedido de recurso foi encaminhado para a <strong>Head Staff</strong> com prioridade <strong>Urgente</strong>.
            <br />Aguarde o contato da nossa equipe.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center w-full px-4 sm:w-auto">
            <Button onClick={() => navigate('/tickets')} variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
              Voltar para Meus Tickets
            </Button>
            {existingAppealTicketId && (
              <Button
                onClick={() => navigate(`/tickets/${existingAppealTicketId}`)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-900/20"
              >
                Ver Ticket de Recurso
              </Button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] p-3 md:p-4 animate-in fade-in zoom-in duration-300">
        <div className="bg-red-950/30 border border-red-500/30 rounded-xl md:rounded-2xl p-6 md:p-8 max-w-2xl w-full shadow-2xl relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute top-0 right-0 p-32 bg-red-600/5 rounded-full blur-3xl -mr-16 -mt-16"></div>

          <div className="flex flex-col items-center text-center relative z-10">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-red-500/10 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 border border-red-500/20 rotate-3 transform shadow-lg shadow-red-900/20">
              <Ban className="w-8 h-8 md:w-10 md:h-10 text-red-500" />
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Criação de Tickets Bloqueada</h1>
            <div className="text-xs md:text-sm font-bold text-red-400 uppercase tracking-widest border border-red-500/20 bg-red-500/10 px-3 py-1 rounded-full mb-4 md:mb-6">
              Ação Administrativa
            </div>

            <p className="text-gray-300 text-base md:text-lg mb-6 md:mb-8 max-w-md px-2">
              Detectamos uma punição ativa em sua conta. Você pode verificar os detalhes e recorrer da decisão no seu painel de identidade.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
              <Button
                onClick={() => navigate('/characters')}
                className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white h-11 md:h-12 px-6 md:px-8 font-bold shadow-lg shadow-indigo-900/20"
              >
                <Gavel className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                Ver Detalhes e Recorrer
              </Button>
              <Button variant="outline" onClick={() => navigate('/tickets')} className="w-full sm:w-auto border-white/10 text-gray-400 hover:text-white hover:bg-white/5 h-11 md:h-12 px-6">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (currentStepId === 'start' && (!categories || categories.length === 0)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh]">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-4" />
        <p className="text-gray-400 animate-pulse">Carregando painel de suporte...</p>
      </div>
    );
  }

  if (showDuplicateWarning) {
    return renderDuplicateWarning();
  }

  if (currentStepId === 'review') {
    return (
      <div className="w-full min-h-[calc(100vh-4rem)] bg-black text-white flex flex-col rounded-xl overflow-hidden shadow-2xl">
        <div className="p-6 flex items-center w-full">
          <Button variant="ghost" size="icon" onClick={handleBack} className="text-gray-400 hover:text-white">
            <ArrowLeft className="w-6 h-6" />
          </Button>
        </div>
        <div className="flex-1 flex flex-col items-center p-6 pb-20">
          {renderReviewStep()}
        </div>
        {isSubmitting && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-[#111] p-8 rounded-2xl border border-white/10 flex flex-col items-center">
              <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
              <p className="text-xl font-bold">Criando Ticket...</p>
              <p className="text-gray-400">Só um momento</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!currentStep) return <div>Erro: Passo não encontrado</div>;

  return (
    <div className="w-full min-h-[calc(100vh-4rem)] bg-gradient-to-br from-black via-gray-900 to-blue-900 animate-gradient-slow text-white flex flex-col items-center relative overflow-hidden rounded-xl shadow-2xl">
      {/* Background Effects */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/10 rounded-full blur-[100px] animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-600/10 rounded-full blur-[100px] animate-pulse delay-700"></div>
      </div>

      {/* Header Minimalista */}
      <div className="p-6 flex items-center justify-between w-full relative z-10">
        {history.length > 0 ? (
          <Button variant="ghost" size="icon" onClick={handleBack} className="text-gray-400 hover:text-white hover:bg-white/5">
            <ArrowLeft className="w-6 h-6" />
          </Button>
        ) : (
          <Button variant="ghost" size="icon" onClick={() => navigate('/tickets')} className="text-gray-400 hover:text-white hover:bg-white/5">
            <X className="w-6 h-6" />
          </Button>
        )}

        <div className="flex items-center gap-4">

        </div>

        {/* Barra de Progresso Simples */}
        <div className="flex-1 mx-8 h-1.5 bg-white/5 rounded-full overflow-hidden backdrop-blur-sm">
          <motion.div
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]"
            initial={{ width: 0 }}
            animate={{ width: `${((history.length + 1) / Math.max(history.length + 2, 5)) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>

        <div className="w-10" /> {/* Spacer para centralizar barra */}
      </div>

      {/* Conteúdo Central */}
      <div className="flex-1 flex flex-col justify-center max-w-7xl mx-auto w-full p-6 pb-20 relative z-10">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStepId}
            custom={direction}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="w-full flex flex-col items-center"
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TicketCreate;
